import React, { useMemo } from 'react';
import { getImageUrl } from '../utils/apiConfig';
import HighlightCard from './HighlightCard';

// Hoist formatter outside component to avoid recreation on each render
const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatMoney = (amount) => moneyFormatter.format(amount || 0);

/**
 * LeaderboardHero - Trends & Highlights dashboard section
 *
 * Shows multiple player spotlights:
 * - Comeback Kid (largest positive rank change)
 * - Cha-Ching (most winnings)
 * - Par 3 Killa (most CTPs)
 * - Notorious S.K.I.N (most skins)
 */
const LeaderboardHero = ({
  players = [],
  getPlayerImageUrl,
  onPlayerClick,
}) => {
  // Calculate highlights and quick stats from player data using single-pass reduce
  const { highlights, quickStats } = useMemo(() => {
    if (!players || players.length === 0) {
      return {
        highlights: { biggestMover: null, moneyLeader: null, par3Killa: null, skinKing: null },
        quickStats: { fedupPlayersCount: 0, totalPot: 0, totalEvents: 0 },
      };
    }

    // Single pass to find all max values and compute stats
    const result = players.reduce(
      (acc, player) => {
        const rankChange = Number(player.rank_change || 0);
        const moneyWon = Number(player.money_won || 0);
        const ctps = Number(player.ctps || 0);
        const skins = Number(player.skins || 0);
        const eventsPlayed = Number(player.events_played || 0);

        // Biggest mover - largest positive rank change
        if (rankChange > 0 && rankChange > (acc.maxRankChange || 0)) {
          acc.maxRankChange = rankChange;
          acc.biggestMover = player;
        }

        // Money leader - most money won
        if (moneyWon > (acc.maxMoney || 0)) {
          acc.maxMoney = moneyWon;
          acc.moneyLeader = player;
        }

        // Par 3 Killa - most CTPs (must have at least 1)
        if (ctps > 0 && ctps > (acc.maxCTPs || 0)) {
          acc.maxCTPs = ctps;
          acc.par3Killa = player;
        }

        // Skin King - most skins
        if (skins > (acc.maxSkins || 0)) {
          acc.maxSkins = skins;
          acc.skinKing = player;
        }

        // Quick stats
        if (player.season_paid) acc.fedupPlayersCount++;
        if (eventsPlayed > acc.maxEvents) acc.maxEvents = eventsPlayed;

        return acc;
      },
      {
        biggestMover: null,
        moneyLeader: null,
        par3Killa: null,
        skinKing: null,
        maxRankChange: 0,
        maxMoney: 0,
        maxCTPs: 0,
        maxSkins: 0,
        fedupPlayersCount: 0,
        maxEvents: 0,
      }
    );

    return {
      highlights: {
        biggestMover: result.biggestMover,
        moneyLeader: result.moneyLeader,
        par3Killa: result.par3Killa,
        skinKing: result.skinKing,
      },
      quickStats: {
        fedupPlayersCount: result.fedupPlayersCount,
        totalPot: (result.fedupPlayersCount * 50) * 0.9, // $50 per paid player, minus 10% for prizes
        totalEvents: result.maxEvents,
      },
    };
  }, [players]);

  const getImageSrc = (player) => {
    if (!player) return '/default-avatar.png';
    if (getPlayerImageUrl) return getPlayerImageUrl(player);
    return getImageUrl(player.image_path);
  };

  const { fedupPlayersCount, totalPot, totalEvents } = quickStats;

  return (
    <div className="mb-6">
      {/* Quick Stats Row */}
      <div className="flex flex-wrap items-center gap-6 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
            {fedupPlayersCount}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">FedUp Players</span>
        </div>
        <div className="w-px h-6 bg-gray-300 dark:bg-navy-600" />
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
            {formatMoney(totalPot)}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">Season Pot</span>
        </div>
        <div className="w-px h-6 bg-gray-300 dark:bg-navy-600 hidden sm:block" />
        <div className="hidden sm:flex items-center gap-2">
          <span className="text-2xl font-bold text-gray-800 dark:text-gray-200 tabular-nums">
            {totalEvents}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">Events</span>
        </div>
      </div>

      {/* Highlights Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <HighlightCard
          title="The Come Up"
          subtitle="Biggest Mover"
          backgroundImage="/assets/come-up.png"
          accentColor="bg-gradient-to-r from-blue-400 to-blue-600"
          player={highlights.biggestMover}
          playerImageSrc={getImageSrc(highlights.biggestMover)}
          stat={highlights.biggestMover ? `+${highlights.biggestMover.rank_change}` : '—'}
          statLabel="Spots"
          statColorClass="text-gray-800 dark:text-gray-200"
          onPlayerClick={onPlayerClick}
        />

        <HighlightCard
          title="Pot Shot Caller"
          subtitle="Most Money Won"
          backgroundImage="/assets/pot-shot-caller.png"
          accentColor="bg-gradient-to-r from-emerald-400 to-emerald-500"
          player={highlights.moneyLeader}
          playerImageSrc={getImageSrc(highlights.moneyLeader)}
          stat={formatMoney(highlights.moneyLeader?.money_won)}
          statLabel="Won"
          statColorClass="text-emerald-600 dark:text-emerald-400"
          onPlayerClick={onPlayerClick}
        />

        <HighlightCard
          title="Par 3 Killa"
          subtitle="Most CTPs"
          backgroundImage="/assets/p3k.png"
          accentColor="bg-gradient-to-r from-orange-400 to-red-500"
          player={highlights.par3Killa}
          playerImageSrc={getImageSrc(highlights.par3Killa)}
          stat={highlights.par3Killa?.ctps || 0}
          statLabel={highlights.par3Killa?.ctps === 1 ? 'CTP' : 'CTPs'}
          statColorClass="text-gray-800 dark:text-gray-200"
          onPlayerClick={onPlayerClick}
        />

        <HighlightCard
          title="Notorious S.K.I.N"
          subtitle="Most Skins Won"
          backgroundImage="/assets/notorious-skin.png"
          accentColor="bg-gradient-to-r from-forest-400 to-forest-600"
          player={highlights.skinKing}
          playerImageSrc={getImageSrc(highlights.skinKing)}
          stat={highlights.skinKing?.skins || 0}
          statLabel="Skins"
          statColorClass="text-forest-600 dark:text-forest-400"
          onPlayerClick={onPlayerClick}
        />
      </div>
    </div>
  );
};

export default LeaderboardHero;
